import {
  Box,
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TextareaAutosize
} from '@mui/material';
import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import toast from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import { useApiService } from "../../services/ApiService/ApiService";

const EvataFleetOnboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selection, setSelection] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [selectedTariff, setSelectedTariff] = useState<string | null>(null);
  const [chargesPerWeek, setChargesPerWeek] = useState('');
  const [avgChargeSize, setAvgChargeSize] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [tariffs, setTariffs] = useState() as any;
  const [loading, setLoading] = useState(true);
  const { userInfo, setUserInfo } = useContext(UserContext);
  const navigate = useNavigate();
  const { fetchWithAuth } = useApiService();
  const steps = ['Energy Requirements', 'Plan Selection', 'Confirmation'];

  const monthlyRequirement = Number(vehicles) * Number(chargesPerWeek) * Number(avgChargeSize) * 4;

  const handleNext = async () => {
    if (activeStep === 0) {
      await fetchTariffs();
    }

    setActiveStep((prev) => prev + 1);
    setShowResults(false);
  };

  const handleBack = () => {
    if (showResults) {
      setShowResults(false);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSelect = (option: string) => {
    setSelection(option);
    setShowResults(false);
  };

  const fetchTariffs = async () => {
    let bodyData: { [name: string]: string } = {};
    bodyData["uuid"] = userInfo.uuid;
    bodyData["businessUuid"] = userInfo.businessUuid;
    bodyData["countryCode"] = countryCode;

    try {
      const response = await fetchWithAuth('/ocpi/tariffs/get', {
        method: 'POST',
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tariffs');
      }

      const res_json = await response.json();
      setTariffs(res_json.tariffs.map((e: any) => ({ ...e, id: e.uuid })));
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Failed to fetch tariffs');
    } finally {
      setLoading(false);
    }
  };

  const createPPA = async () => {
    let bodyData: { [name: string]: string } = {};
    bodyData["uuid"] = userInfo.uuid;
    bodyData["businessUuid"] = userInfo.businessUuid;
    bodyData["tariffCountryVersionUuid"] = selectedTariff!;
    bodyData["totalMonthlyPublicKWh"] = monthlyRequirement.toString();

    try {
      const response = await fetchWithAuth('/ppa/advanced/build/agreement/create', {
        method: 'POST',
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        throw new Error('Failed to create PPA');
      }

      const res_json = await response.json();
      toast.success('Power Purchase Agreement setup Successful', {
        position: 'bottom-center',
        duration: 5000,
      });
      navigate("/agree/ppa/list");
    } catch (error) {
      console.error('Error creating PPA:', error);
      toast.error('Failed to create Power Purchase Agreement');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ maxWidth: 500, mx: 'auto' }}>
            <TextField label="Country Code" margin="normal" sx={{ width: '100%' }} value={countryCode} onChange={(e) => setCountryCode(e.target.value)} />
            <TextField label="Approx. Number of Vehicles" margin="normal" sx={{ width: '100%' }} value={vehicles} onChange={(e) => setVehicles(e.target.value)} />
            <TextField label="Charges per week" margin="normal" sx={{ width: '100%' }} value={chargesPerWeek} onChange={(e) => setChargesPerWeek(e.target.value)} />
            <TextField label="Average Charge size (kW/h)" margin="normal" sx={{ width: '100%' }} value={avgChargeSize} onChange={(e) => setAvgChargeSize(e.target.value)} />
            <Typography sx={{ mt: 2 }}>
              Monthly Requirement: <strong>{monthlyRequirement.toFixed(2)} kW/h</strong>
            </Typography>
          </Box>
        );
      case 1:
        if (!showResults) {
          return (
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
              <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Charge Point Operator</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Idle Fee</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Coverage</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tariffs && tariffs.map((tariff: any) => (
                      <TableRow key={tariff.tariffCountryVersionUuid} selected={selectedTariff === tariff.tariffCountryVersionUuid}>
                        <TableCell>
                          {tariff.logoUrl ? (
                            <img
                              src={tariff.logoUrl}
                              alt={tariff.cpoName}
                              style={{ height: 24, objectFit: 'contain' }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{tariff.cpoName}</TableCell>
                        <TableCell>{tariff.ratePerKWh ? `£${tariff.ratePerKWh}/kWh` : 'N/A'}</TableCell>
                        <TableCell>{tariff.accessFeeMonthly ? `£${tariff.accessFeeMonthly}/month` : 'N/A'}</TableCell>
                        <TableCell>
                          <Button
                            variant={selectedTariff === tariff.tariffCountryVersionUuid ? 'contained' : 'outlined'}
                            onClick={() => {
                              setSelectedTariff(tariff.tariffCountryVersionUuid);
                              setActiveStep((prev) => prev + 1);
                            }}
                          >
                            {selectedTariff === tariff.tariffCountryVersionUuid ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }
        return (<></>);
      case 2:
        return (
          <Box sx={{ maxWidth: 500, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Confirmation
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Monthly Energy Requirement
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {monthlyRequirement.toFixed(2)} kW/h
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Terms and Conditions
              </Typography>
              <TextareaAutosize
                minRows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: 'inherit',
                  fontSize: '14px'
                }}
                defaultValue={`By signing up, you agree to:

1. Share your fleet's charging data with our platform
2. Allow us to analyze your energy consumption patterns
3. Receive notifications about optimal charging times and rates
4. Maintain the confidentiality of your account credentials
5. Comply with all applicable charging network terms and conditions

You can cancel your subscription at any time.`}
                readOnly
              />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{
        width: '100%',
        maxWidth: 1000,
        mx: 'auto',
        my: 6,
        px: 4,
        py: 6,
        boxShadow: 3,
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>
        {renderStepContent(activeStep)}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', maxWidth: 500, mx: 'auto' }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>
          {activeStep !== 1 && (
            <Button
              variant="contained"
              onClick={activeStep === steps.length - 1 ? createPPA : handleNext}
              disabled={
                (activeStep === 0 && (!countryCode || !vehicles || !chargesPerWeek || !avgChargeSize))
              }
            >
              {activeStep === steps.length - 1 ? 'Sign Up' : 'Next'}
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default EvataFleetOnboarding;
